import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock3 } from 'lucide-react';
import MetricCard from '../dashboard/MetricCard';
import SalesByPeriodCard from './SalesByPeriodCard';
import { formatTimeAU } from '../../shared/tenantClock';

// Real, live-data dashboard. Reuses the same dashboard-page-* shell classes
// (App.css) and MetricCard every other dashboard in the app already uses,
// but every number here comes from GET /api/sales-orders — no new backend
// endpoint needed, since salesOrderController.listSalesOrders already
// supports the filters this needs (status, backorder). The fulfilment
// queue (Ready to Pick, Pack and Ship) is on the Warehouse Dashboard.
function SalesDashboard() {
  const navigate = useNavigate();
  const [openOrders, setOpenOrders] = useState(null);
  const [backorderTotal, setBackorderTotal] = useState(null);
  const [backorders, setBackorders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(new Date());
  const [refreshKey, setRefreshKey] = useState(0);

  const loadDashboard = () => {
    setLoading(true);
    Promise.all([
      axios.get('/api/sales-orders', { params: { status: 'draft', limit: 1 } }),
      // Oldest-first — a backorder that's been sitting the longest is the
      // one most worth surfacing first, not the most recently created.
      axios.get('/api/sales-orders', { params: { backorder: 'true', limit: 10, sortBy: 'created_at', sortOrder: 'ASC' } }),
    ])
      .then(([openRes, backorderRes]) => {
        setOpenOrders(openRes.data.pagination?.total ?? 0);
        setBackorderTotal(backorderRes.data.pagination?.total ?? 0);
        setBackorders(backorderRes.data.data || []);
      })
      .catch(() => {
        setOpenOrders(0);
        setBackorderTotal(0);
        setBackorders([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = () => {
    setLastRefreshedAt(new Date());
    setRefreshKey((k) => k + 1);
    loadDashboard();
  };

  const formatRefreshTime = formatTimeAU;

  return (
    <div className="dashboard dashboard-page">
      <div className="dashboard-content">
        <div className="dashboard-content-inner">
          <div className="dashboard-page-header">
            <div className="dashboard-page-header-main">
              <h2 className="dashboard-page-title">Sales Dashboard</h2>
              <p className="dashboard-page-description">Open orders, sales by period, and backorders needing attention.</p>
            </div>
            <div className="dashboard-page-controls">
              <span className="dashboard-page-meta">
                <Clock3 size={14} /> Last refresh: {formatRefreshTime(lastRefreshedAt)}
              </span>
              <button className="dashboard-refresh-btn" type="button" onClick={handleRefresh}>Refresh</button>
            </div>
          </div>

          <div className="metrics-section">
            <h2>Key Metrics</h2>
            <div className="metrics-grid">
              <MetricCard
                title="Open Orders"
                value={loading ? '—' : openOrders}
                iconName="Open Orders"
                onClick={() => navigate('/sales-orders', { state: { initialFilters: { status: 'draft' } } })}
              />
              <MetricCard
                title="Backorders"
                value={loading ? '—' : backorderTotal}
                iconName="Backorders"
                onClick={() => navigate('/sales-orders', { state: { initialFilters: { backorder: true } } })}
              />
            </div>
          </div>

          <SalesByPeriodCard refreshKey={refreshKey} />

          {/* Heading outside the card, like the sections above. */}
          <div className="metrics-section">
            <h2>Backorders</h2>
            <div style={{ background: 'var(--bg-white)', border: '1px solid var(--border-color)', borderRadius: '0.75rem', boxShadow: 'var(--shadow-sm)', padding: 16 }}>
            <div className="deadlines-list">
              {loading ? (
                <div className="dashboard-empty-state">Loading…</div>
              ) : backorders.length === 0 ? (
                <div className="dashboard-empty-state">No backorders — every open Sales Order is either not started or fully shipped.</div>
              ) : (
                backorders.map((so) => {
                  const total = Number(so.lines_qty_total || 0);
                  const shipped = Number(so.lines_qty_shipped_total || 0);
                  return (
                    <div
                      key={so.id}
                      onClick={() => navigate(`/sales-orders/${so.id}`)}
                      style={{ background: '#f8fafc', border: '1px solid #e3e7ee', borderRadius: 8, padding: '10px 14px', marginBottom: 8, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{so.display_number}</div>
                        <div style={{ fontSize: 12, color: '#888' }}>{so.customer?.name}</div>
                      </div>
                      <div style={{ fontSize: 13, color: '#c98a1f', fontWeight: 600 }}>{shipped} of {total} shipped</div>
                    </div>
                  );
                })
              )}
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SalesDashboard;
